<template>
  <div class="bg-white shadow-md rounded-xl p-6 h-full flex flex-col">
    <!-- Panel Header -->
    <div class="flex justify-between items-center border-b pb-4">
      <h2 class="text-xl font-semibold text-gray-800">Episode Management</h2>
      <!-- START: CORRECTED BUTTON COLOUR -->
      <button @click="openModalForCreate" class="bg-brand-purple-dark text-white py-2 px-4 rounded-md hover:opacity-90 text-sm font-semibold">
        Create New Episode
      </button>
      <!-- END: CORRECTED BUTTON COLOUR -->
    </div>

    <!-- Episode List -->
    <div class="mt-4 flex-grow">
      <div v-if="loading" class="space-y-3 animate-pulse"><div v-for="i in 2" :key="i" class="p-3 bg-gray-100 rounded-md h-16"></div></div>
      <div v-else-if="episodes.length === 0" class="text-center text-gray-500 h-full flex flex-col justify-center items-center py-4">
        <svg class="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path></svg>
        <p class="mt-2 font-semibold text-gray-700">No episodes yet</p>
        <p class="text-sm">Click "New Episode" to get started.</p>
      </div>
      <ul v-else class="space-y-3">
        <li v-for="episode in episodes" :key="episode.id" class="p-3 bg-gray-50 rounded-md flex items-center space-x-4">
          <img :src="episode.episodeCoverArtUrl || (clientData && clientData.coverArtUrl)" alt="Episode Artwork" class="w-16 h-16 rounded-md object-cover flex-shrink-0 bg-gray-200">
          <div class="flex-grow">
            <p class="font-semibold text-gray-700">{{ episode.title }}</p>
            <div class="flex items-center space-x-2 mt-1">
              <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize" :class="episode.status === 'published' ? 'bg-status-completed-bg text-status-completed-text' : 'bg-gray-200 text-gray-600'">{{ episode.status }}</span>
              <span v-if="episode.aiStatus === 'processing'" class="flex items-center text-xs font-semibold text-blue-600">
                <svg class="animate-spin -ml-1 mr-1 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                AI Processing...
              </span>
              <span v-else-if="episode.aiStatus === 'failed'" class="flex items-center text-xs font-semibold text-red-600" title="AI processing failed for this episode.">
                <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path></svg>
                AI Failed
              </span>
            </div>
          </div>
          <div class="flex items-center space-x-4 flex-shrink-0">
            <button @click="openModalForEdit(episode)" class="text-sm font-medium text-brand-purple-dark hover:text-brand-pink-static">Edit</button>
            <button @click="deleteEpisode(episode)" class="text-sm font-medium text-red-600 hover:text-red-800">Delete</button>
          </div>
        </li>
      </ul>
    </div>

    <!-- Create/Edit Episode Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-start p-4 overflow-y-auto">
      <div class="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mt-12">
        <h3 class="text-lg font-bold text-gray-900 border-b pb-3">{{ isEditing ? 'Edit Episode' : 'Create New Episode' }}</h3>
        <form @submit.prevent class="mt-4 space-y-4">
          <div>
            <label for="episodeTitle" class="block text-sm font-medium text-gray-700">Episode Title</label>
            <input v-model="episodeForm.title" type="text" id="episodeTitle" class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand-purple focus:border-brand-purple">
          </div>
          
          <div>
            <div class="flex justify-between items-center">
              <label for="showNotes" class="block text-sm font-medium text-gray-700">Show Notes / Description</label>
              <button @click="generateShowNotesAI" :disabled="!episodeForm.title || isGeneratingNotes" type="button" class="flex items-center text-sm font-medium text-brand-purple-dark hover:text-brand-pink-static disabled:opacity-50 disabled:cursor-not-allowed">
                <!-- ... AI generate button icons ... -->
                <span>{{ isGeneratingNotes ? 'Generating...' : 'Generate with AI' }}</span>
              </button>
            </div>
            <textarea v-model="episodeForm.showNotes" id="showNotes" rows="6" class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand-purple focus:border-brand-purple"></textarea>
          </div>

          <div v-if="isEditing && episodeForm.transcript">
            <label for="transcript" class="block text-sm font-medium text-gray-700">AI-Generated Transcript (Read-only)</label>
            <textarea id="transcript" :value="episodeForm.transcript" readonly rows="8" class="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 focus:ring-brand-purple focus:border-brand-purple"></textarea>
          </div>

          <div>
            <label for="artworkFile" class="block text-sm font-medium text-gray-700">Episode Cover Art (Optional)</label>
            <p v-if="isEditing" class="text-xs text-gray-500 mb-2">You can optionally replace the existing episode cover art.</p>
            <div class="flex items-center space-x-4 mt-2">
              <img v-if="artworkPreviewUrl || episodeForm.episodeCoverArtUrl" :src="artworkPreviewUrl || episodeForm.episodeCoverArtUrl" alt="Artwork Preview" class="w-24 h-24 rounded-md object-cover">
              <input @change="handleArtworkFileSelect" type="file" id="artworkFile" accept="image/jpeg,image/png" class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-brand-orange hover:file:bg-orange-100">
            </div>
            <p v-if="episodeForm.episodeCoverArtFile" class="mt-2 text-xs text-gray-600">Selected: {{ episodeForm.episodeCoverArtFile.name }}</p>
          </div>

          <div>
            <label for="mediaFile" class="block text-sm font-medium text-gray-700">Episode File (MP3 or MP4)</label>
            <p v-if="isEditing" class="text-xs text-gray-500 mb-2">You can optionally replace the existing media file.</p>
            <input @change="handleMediaFileSelect" type="file" id="mediaFile" accept=".mp4,.mp3" class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-brand-orange hover:file:bg-orange-100">
            <p v-if="episodeForm.mediaFile" class="mt-2 text-xs text-gray-600">Selected: {{ episodeForm.mediaFile.name }}</p>
          </div>

          <div v-if="isSaving" class="text-center p-4 bg-gray-50 rounded-md"><p class="font-semibold animate-pulse">{{ feedbackMessage }}</p></div>
          
          <div class="mt-6 flex justify-end space-x-3">
            <button @click="closeModal" type="button" :disabled="isSaving" class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50">Cancel</button>
            <template v-if="isEditing">
              <button @click="saveEpisodeChanges()" type="button" :disabled="isSaving" class="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50">Save Changes</button>
              <button v-if="currentEpisodeStatus === 'draft'" @click="saveEpisodeChanges('published')" type="button" :disabled="isSaving" class="px-4 py-2 bg-brand-purple-dark text-white rounded-md hover:opacity-90 disabled:opacity-50">Publish</button>
              <button v-if="currentEpisodeStatus === 'published'" @click="saveEpisodeChanges('draft')" type="button" :disabled="isSaving" class="px-4 py-2 bg-yellow-500 text-white rounded-md hover:opacity-90 disabled:opacity-50">Unpublish</button>
            </template>
            <template v-else>
              <button @click="saveNewEpisode('draft')" type="button" :disabled="isSaving" class="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50">Save as Draft</button>
              <button @click="saveNewEpisode('published')" type="button" :disabled="isSaving" class="px-4 py-2 bg-brand-purple-dark text-white rounded-md hover:opacity-90 disabled:opacity-50">Publish Now</button>
              <button @click="createEpisodeWithAI" type="button" :disabled="isSaving" class="px-4 py-2 bg-brand-purple-dark text-white rounded-md hover:opacity-90 disabled:opacity-50">Create with AI</button>
            </template>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { db, storage } from '@/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, addDoc, doc, updateDoc, serverTimestamp, query, where, onSnapshot, orderBy, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { mapState } from 'vuex';
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'EpisodeManagementPanel',
  data() {
    return {
      episodes: [],
      clientData: null,
      loading: true,
      unsubscribeEpisodes: null,
      showModal: false,
      episodeForm: { id: null, title: '', showNotes: '', transcript: null, mediaFile: null, episodeCoverArtFile: null, episodeCoverArtUrl: null },
      isSaving: false,
      feedbackMessage: '',
      isEditing: false,
      isGeneratingNotes: false,
      currentEpisodeStatus: null,
      artworkPreviewUrl: null,
    };
  },
  computed: { ...mapState(['clientId', 'authIsReady']) },
  methods: {
    async deleteEpisode(episode) {
      if (!confirm('Are you sure you want to permanently delete this episode?')) { return; }
      try {
        const functions = getFunctions(undefined, 'europe-west2');
        const deleteEpisodeFunction = httpsCallable(functions, 'deleteEpisode');
        await deleteEpisodeFunction({
          episodeId: episode.id,
          mediaUrl: episode.mediaUrl,
          episodeCoverArtUrl: episode.episodeCoverArtUrl
        });
      } catch (error) {
        console.error("Error deleting episode:", error);
        alert('An error occurred while trying to delete the episode.');
      }
    },
    async generateShowNotesAI() {
      if (!this.episodeForm.title) { alert('Please enter an episode title before generating show notes.'); return; }
      this.isGeneratingNotes = true;
      try {
        const functions = getFunctions(undefined, 'europe-west2'); 
        const generateShowNotes = httpsCallable(functions, 'generateShowNotes');
        const result = await generateShowNotes({
          episodeTitle: this.episodeForm.title,
          episodeDescription: this.episodeForm.showNotes,
        });
        if (result.data && result.data.showNotes) {
          this.episodeForm.showNotes = result.data.showNotes;
        }
      } catch (error) {
        console.error("Error generating AI show notes:", error);
        alert('An error occurred while generating the show notes.');
      } finally {
        this.isGeneratingNotes = false;
      }
    },
    openModalForCreate() {
      this.isEditing = false;
      this.currentEpisodeStatus = null;
      this.artworkPreviewUrl = null;
      this.episodeForm = { id: null, title: '', showNotes: '', transcript: null, mediaFile: null, episodeCoverArtFile: null, episodeCoverArtUrl: null };
      document.getElementById('mediaFile')?.form.reset();
      document.getElementById('artworkFile')?.form.reset();
      this.showModal = true;
    },
    openModalForEdit(episode) {
      this.isEditing = true;
      this.currentEpisodeStatus = episode.status;
      this.artworkPreviewUrl = null;
      this.episodeForm = {
        id: episode.id,
        title: episode.title,
        showNotes: episode.showNotes,
        transcript: episode.transcript,
        mediaFile: null,
        episodeCoverArtFile: null,
        episodeCoverArtUrl: episode.episodeCoverArtUrl,
      };
      document.getElementById('mediaFile')?.form.reset();
      document.getElementById('artworkFile')?.form.reset();
      this.showModal = true;
    },
    closeModal() {
      if (this.isSaving) return;
      this.showModal = false;
      this.isEditing = false;
      this.currentEpisodeStatus = null;
      if (this.artworkPreviewUrl) {
        URL.revokeObjectURL(this.artworkPreviewUrl);
        this.artworkPreviewUrl = null;
      }
    },
    handleMediaFileSelect(event) {
      this.episodeForm.mediaFile = event.target.files[0] || null;
    },
    handleArtworkFileSelect(event) {
      const file = event.target.files[0];
      if (!file) {
        this.episodeForm.episodeCoverArtFile = null;
        this.artworkPreviewUrl = null;
        return;
      }
      this.episodeForm.episodeCoverArtFile = file;
      this.artworkPreviewUrl = URL.createObjectURL(file);
    },
    async createEpisodeWithAI() {
      if (!this.episodeForm.mediaFile) {
        alert('Please select an MP3 or MP4 file to create with AI.');
        return;
      }
      this.isSaving = true;
      let createdEpisodeRef;
      try {
        this.feedbackMessage = 'Preparing AI episode record...';
        const file = this.episodeForm.mediaFile;
        const initialEpisodeData = {
          clientId: this.clientId,
          title: 'New Episode - AI is generating content...',
          showNotes: 'Please wait a few moments for the AI to process the audio.',
          mediaUrl: '',
          status: 'draft',
          createdAt: serverTimestamp(),
          mediaType: file.type,
          mediaSize: file.size,
          aiStatus: 'pending',
        };
        createdEpisodeRef = await addDoc(collection(db, 'episodes'), initialEpisodeData);
        this.feedbackMessage = 'Uploading media for AI analysis...';
        const fileExtension = file.name.split('.').pop();
        const fileName = `episode-files/${this.clientId}/${createdEpisodeRef.id}.${fileExtension}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, file);
        const mediaUrl = await getDownloadURL(storageRef);
        this.feedbackMessage = 'Finalizing and starting AI...';
        await updateDoc(doc(db, 'episodes', createdEpisodeRef.id), { mediaUrl: mediaUrl });
      } catch (error) {
        console.error("Error creating AI episode:", error);
        if (createdEpisodeRef) {
          await updateDoc(doc(db, 'episodes', createdEpisodeRef.id), { aiStatus: 'failed', aiError: 'Upload or processing failed.' });
        }
        alert('An error occurred during the upload. Please try again.');
      } finally {
        this.isSaving = false;
        this.feedbackMessage = '';
        this.closeModal();
      }
    },
    async saveNewEpisode(status) {
      if (!this.episodeForm.title || !this.episodeForm.mediaFile) {
        alert('For manual creation, please provide a title and select an MP3 or MP4 file.');
        return;
      }
      this.isSaving = true;
      try {
        this.feedbackMessage = 'Uploading artwork (if any)...';
        let artworkUrl = null;
        if (this.episodeForm.episodeCoverArtFile) {
          const artFile = this.episodeForm.episodeCoverArtFile;
          const artExt = artFile.name.split('.').pop();
          const artFileName = `episode-artwork/${this.clientId}/${uuidv4()}.${artExt}`;
          const artStorageRef = ref(storage, artFileName);
          await uploadBytes(artStorageRef, artFile);
          artworkUrl = await getDownloadURL(artStorageRef);
        }
        this.feedbackMessage = 'Uploading media file...';
        const file = this.episodeForm.mediaFile;
        const fileExtension = file.name.split('.').pop();
        const fileName = `episode-files/${this.clientId}/${uuidv4()}.${fileExtension}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, file);
        const mediaUrl = await getDownloadURL(storageRef);
        this.feedbackMessage = 'Saving episode details...';
        const episodeData = {
          clientId: this.clientId, title: this.episodeForm.title, showNotes: this.episodeForm.showNotes,
          mediaUrl: mediaUrl, status: status, createdAt: serverTimestamp(),
          mediaType: file.type, mediaSize: file.size,
          episodeCoverArtUrl: artworkUrl,
        };
        await addDoc(collection(db, 'episodes'), episodeData);
      } catch (error) {
        console.error("Error saving new episode:", error);
        alert('An error occurred. Please try again.');
      } finally {
        this.isSaving = false;
        this.feedbackMessage = '';
        this.closeModal();
      }
    },
    async saveEpisodeChanges(newStatus = null) {
      if (!this.episodeForm.title) {
        alert('Please provide a title.');
        return;
      }
      this.isSaving = true;
      this.feedbackMessage = 'Updating episode...';
      try {
        const updatedData = {
          title: this.episodeForm.title,
          showNotes: this.episodeForm.showNotes,
        };
        if (this.episodeForm.episodeCoverArtFile) {
          this.feedbackMessage = 'Replacing artwork...';
          const artFile = this.episodeForm.episodeCoverArtFile;
          const artExt = artFile.name.split('.').pop();
          const artFileName = `episode-artwork/${this.clientId}/${uuidv4()}.${artExt}`;
          const artStorageRef = ref(storage, artFileName);
          await uploadBytes(artStorageRef, artFile);
          updatedData.episodeCoverArtUrl = await getDownloadURL(artStorageRef);
        }
        if (this.episodeForm.mediaFile) {
          this.feedbackMessage = 'Replacing media file...';
          const file = this.episodeForm.mediaFile;
          const fileExtension = file.name.split('.').pop();
          const fileName = `episode-files/${this.clientId}/${uuidv4()}.${fileExtension}`;
          const storageRef = ref(storage, fileName);
          await uploadBytes(storageRef, file);
          updatedData.mediaUrl = await getDownloadURL(storageRef);
          updatedData.mediaType = file.type;
          updatedData.mediaSize = file.size;
        }
        if (newStatus) {
          updatedData.status = newStatus;
        }
        this.feedbackMessage = 'Saving changes...';
        const episodeRef = doc(db, 'episodes', this.episodeForm.id);
        await updateDoc(episodeRef, updatedData);
      } catch (error) {
        console.error("Error updating episode:", error);
        alert('An error occurred. Please try again.');
      } finally {
        this.isSaving = false;
        this.feedbackMessage = '';
        this.closeModal();
      }
    },
    fetchEpisodes() {
      if (!this.clientId) return;
      this.loading = true;
      const q = query(collection(db, 'episodes'), where('clientId', '==', this.clientId), orderBy('createdAt', 'desc'));
      this.unsubscribeEpisodes = onSnapshot(q, (snapshot) => {
        this.episodes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        this.loading = false;
      }, (error) => {
        console.error("Error fetching episodes:", error); this.loading = false;
      });
    },
    async fetchClientData() {
      if (!this.clientId) return;
      const clientRef = doc(db, 'clients', this.clientId);
      const clientSnap = await getDoc(clientRef);
      if (clientSnap.exists()) {
        this.clientData = clientSnap.data();
      } else {
        console.error("Could not fetch client data for artwork fallback.");
      }
    }
  },
  watch: {
    authIsReady(isReady) {
      if (isReady && !this.unsubscribeEpisodes) {
        this.fetchClientData();
        this.fetchEpisodes();
      }
    }
  },
  created() {
    if (this.authIsReady) {
      this.fetchClientData();
      this.fetchEpisodes();
    }
  },
  beforeUnmount() {
    if (this.unsubscribeEpisodes) {
      this.unsubscribeEpisodes();
    }
  }
};
</script>